import { faFolder } from '@fortawesome/pro-light-svg-icons/faFolder'
import { faHouseBlank } from '@fortawesome/pro-light-svg-icons/faHouseBlank'
import { faTrophyStar } from '@fortawesome/pro-light-svg-icons/faTrophyStar'
import type { MenuItemProps } from '@space-core/components/structure/menu/menuItem/types'
import { Actions, Entities } from '@space-core/datahub'
import type { DatahubEventProps } from '@space-core/datahub'
import { openGamificationEventDispatch } from '@space-events/openGamification'
import { useDefaultProductRequest } from '@space-shell/common/hooks/useDefaultProductRequest'
import type { GoogleAnalyticsProps } from '@space-shell/common/hooks/useGoogleAnalytics'
import { useOptionalShowcase } from '@space-shell/common/hooks/useOptionalShowcase'
import { UrlUtils } from '@space-shell/common/utils/url'

import { RoutesPaths } from '@space-shell/core/router'
import type { TFunction } from 'react-i18next'
import { generatePath, type NavigateFunction } from 'react-router-dom'

export type GetMenuItemsProps = {
  t: TFunction
  slug: string
  navigate: NavigateFunction
  pathname: string
  language: string
  sendDataHubEvent: (dataHubProps: DatahubEventProps) => void
  sendGoogleAnalyticsEvent: (GoogleAnalyticsProps: GoogleAnalyticsProps) => void
  isGamificationEnabled?: boolean
}

const useMenuItems = ({
  t,
  slug,
  navigate,
  pathname,
  language,
  sendDataHubEvent,
  sendGoogleAnalyticsEvent,
  isGamificationEnabled
}: GetMenuItemsProps): MenuItemProps[] => {
  const normalizedLanguage = String(language).toLowerCase()
  const defaultProductRequest = useDefaultProductRequest()
  const { showcaseEnabled } = useOptionalShowcase()

  const handleHomeClick = () => {
    sendDataHubEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU_BEGIN
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU_BEGIN
    })

    const homePath = `/${normalizedLanguage}/club/${slug}`

    if (!showcaseEnabled) {
      window.location.replace(homePath)

      return
    }

    navigate(homePath)
  }

  const handleProductClick = () => {
    sendDataHubEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU_LIBRARY
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU_LIBRARY
    })

    if (defaultProductRequest?.data?.purchased) {
      const contentConsumePath = generatePath(`${RoutesPaths.HOME}${RoutesPaths.CONTENT_CONSUME}`, {
        lang: normalizedLanguage,
        membership: slug,
        productId: String(defaultProductRequest.data.id)
      })

      /**
       * Note: It is necessary to reload the page, as the content consumption
       * micro-frontend router is outside the context of the host application
       */
      window.location.replace(contentConsumePath)

      return
    }

    if (defaultProductRequest?.data?.clubSalesPage.pageOnline) {
      const salesPagePath = generatePath(`${RoutesPaths.HOME}${RoutesPaths.SALES}`, {
        lang: normalizedLanguage,
        membership: slug,
        slug: defaultProductRequest.data.clubSalesPage.slug,
        referenceCode: defaultProductRequest.data.affiliationCode
      })

      navigate(salesPagePath)

      return
    }

    navigate(`/${normalizedLanguage}/club/${slug}${RoutesPaths.PRODUCTS}`)
  }

  const handleGamificationClick = () => {
    openGamificationEventDispatch({ arrivedFrom: 'CLUB_SIDE_MENU' })
    navigate(`/${normalizedLanguage}/club/${slug}${RoutesPaths.GAMIFICATION}`)
  }

  const menuItems = [
    {
      label: t('menu.home'),
      iconPrimary: faHouseBlank,
      onClick: () => handleHomeClick(),
      isActive: UrlUtils.isMenuItemActive(RoutesPaths.HOME, pathname),
      visible: showcaseEnabled
    },
    {
      label: !showcaseEnabled ? t('menu.product') : t('menu.products'),
      isActive: UrlUtils.isMenuItemActive(
        `${RoutesPaths.HOME}${RoutesPaths.PRODUCTS}`,
        pathname,
        false
      ),
      className: '_mb-4',
      iconPrimary: faFolder,
      onClick: () => handleProductClick(),
      visible: true
    }
  ]

  if (isGamificationEnabled) {
    menuItems.push({
      label: t('menu.gamification'),
      iconPrimary: faTrophyStar,
      onClick: () => handleGamificationClick(),
      isActive: UrlUtils.isMenuItemActive(
        `${RoutesPaths.HOME}${RoutesPaths.GAMIFICATION}`,
        pathname,
        false
      ),
      visible: true
    })
  }

  return menuItems
}

export default useMenuItems
